// Checkout.js
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FaMobile, FaCreditCard, FaTruck } from "react-icons/fa";
import ContactInfo from "../components/ContactInfo";
import ShippingInfo from "../components/ShippingInfo";
import OrderSummary from "../components/Ordersummary";
import { fetchCart, clearCart } from "../actions/cartActions";
import { placeOrder } from "../actions/ordersActions";
import { getAddressbyUserId } from "../actions/addressActions";
import { clearCoupons } from "../actions/couponActions";
import { useNavigate } from "react-router-dom";
import "../styles/checkout.scss";
import "../styles/paymentmodal.css";
import ProgressIndicator from "../components/ProgressIndicator";
import ShippingFees from "../components/ShippingFees";
import { $isLoggedIn, $logoutUser } from "../globals";
import formatMoney from "../components/formatMoney";

const Checkout = () => {
  const [contactData, setContactData] = useState({});
  const [activeSections, setActiveSections] = useState({
    shipping: false,
    contact: true,
  });
  const [showRedirectPopup, setShowRedirectPopup] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showPaymentMethodSelection, setShowPaymentMethodSelection] =
    useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [orderResponse, setOrderResponse] = useState(null);
  const [showMobileMoneyInstructions, setShowMobileMoneyInstructions] =
    useState(false);
  const [orderAmount, setOrderAmount] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: cartItems = [] } = useSelector((state) => state.cart);
  const { addresses = [] } = useSelector((state) => state.address);
  const { appliedCoupons = [] } = useSelector((state) => state.coupons);
  const [defaultAddress, setDefaultAddress] = useState(null);

  const cartTotal = cartItems.reduce(
    (total, item) =>
      total + (parseFloat(item.product.price) || 0) * (item.quantity || 1),
    0,
  );

  useEffect(() => {
    dispatch(fetchCart());
    dispatch(getAddressbyUserId());
    if (!$isLoggedIn()) {
      $logoutUser();
      navigate("/signin");
    }
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!defaultAddress) {
      alert("Please add a shipping address");
      return;
    }

    if (!contactData.primaryPhone) {
      alert("Please add contact information");
      return;
    }

    setIsPlacingOrder(true);

    try {
      const addressString = `${defaultAddress.address}, ${
        defaultAddress.city
      }, ${defaultAddress.country}${
        contactData.secondaryPhone
          ? `, Secondary Phone: ${contactData.secondaryPhone}`
          : ""
      }`;

      const response = await dispatch(placeOrder({ address: addressString }));

      // Store the order response and show initial popup
      if (response) {
        // Store the order amount before clearing cart
        setOrderAmount(cartTotal);
        setOrderResponse(response);
        setShowRedirectPopup(true);
      } else {
        dispatch(clearCart());
        dispatch(clearCoupons()); // Clear applied coupons after successful order
        navigate("/orders");
      }
    } catch (error) {
      console.error("Error placing order:", error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const toggleSection = (section) => {
    switch (section) {
      case "shipping":
        setActiveSections({
          ...activeSections,
          [section]: !activeSections[section],
        });
        break;
      case "contact":
        setActiveSections({
          ...activeSections,
          [section]: !activeSections[section],
        });
        break;
      case "both":
        setActiveSections({
          ...activeSections,
          [section]: !activeSections[section],
        });
        break;
      default:
        setActiveSections({
          ...activeSections,
          [section]: !activeSections[section],
        });
    }
  };

  const sectionIsActive = (section) => {
    return activeSections[section];
  };

  const isSectionFilled = (section) => {
    if (section === "shipping") {
      return !!defaultAddress;
    }
    if (section === "contact") {
      return !!contactData.primaryPhone;
    }
    return false;
  };

  const contactAddress = () => {
    return `${defaultAddress?.address}, ${defaultAddress?.city}, ${defaultAddress?.country}, Postal Code: ${defaultAddress?.postal_code}`;
  };

  useEffect(() => {
    if (addresses.length > 0) {
      const defaultAddress = addresses.find((address) => address.is_default);
      if (defaultAddress) {
        setDefaultAddress(defaultAddress);
      }
    }
  }, [addresses]);

  const refetchAddress = () => {
    dispatch(getAddressbyUserId());
  };

  const getImage = (item, image) => {
    if (item.option_id && item.option && item.option.image) {
      return item.option.image;
    }
    return image;
  };

  const handleRedirectPopupClose = () => {
    setShowRedirectPopup(false);
    setRedirectUrl("");
    dispatch(clearCart());
    dispatch(clearCoupons()); // Clear applied coupons after successful order
    navigate("/orders");
  };

  const handlePayNowClick = () => {
    setShowRedirectPopup(false);
    setShowPaymentMethodSelection(true);
  };

  const handlePaymentMethodSelection = (method) => {
    setSelectedPaymentMethod(method);

    if (method === "card") {
      // For card payment, redirect directly to the payment URL
      if (
        orderResponse &&
        orderResponse.submit_order_response &&
        orderResponse.submit_order_response.redirect_url
      ) {
        window.open(orderResponse.submit_order_response.redirect_url, "_blank");
        handlePaymentMethodClose();
      }
    } else if (method === "mobile_money") {
      // For mobile money, show the USSD instructions
      setShowPaymentMethodSelection(false);
      setShowMobileMoneyInstructions(true);
    }
  };

  const handlePaymentMethodClose = () => {
    setShowPaymentMethodSelection(false);
    setSelectedPaymentMethod(null);
    setOrderResponse(null);
    dispatch(clearCart());
    dispatch(clearCoupons());
    navigate("/orders");
  };

  const handleMobileMoneyInstructionsClose = () => {
    setShowMobileMoneyInstructions(false);
    dispatch(clearCart());
    dispatch(clearCoupons());
    navigate("/orders");
  };

  const handleBackToPaymentMethods = () => {
    setShowMobileMoneyInstructions(false);
    setShowPaymentMethodSelection(true);
  };

  useEffect(() => {
    document.title = "Checkout";
  }, []);

  // Calculate expected arrival date (2 weeks from today)
  const expectedArrival = useMemo(() => {
    const today = new Date();
    const arrivalDate = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    return {
      dayName: arrivalDate.toLocaleDateString("en-US", { weekday: "long" }),
      date: arrivalDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    };
  }, []);

  return (
    <div className="checkout-page">
      <ProgressIndicator currentStep={2} />

      {/* Payment Method Selection Popup */}
      {showPaymentMethodSelection && (
        <div className="payment-method-selection-overlay">
          <div className="payment-method-selection-modal">
            <div className="payment-method-selection-header">
              <h3>Choose Payment Method</h3>
              <button
                className="payment-method-selection-close"
                onClick={handlePaymentMethodClose}
              >
                ×
              </button>
            </div>
            <div className="payment-method-selection-content">
              <p>
                Your order has been placed successfully. Please choose your
                preferred payment method:
              </p>
              <div className="payment-methods">
                <button
                  className="payment-method-btn mobile-money-btn"
                  onClick={() => handlePaymentMethodSelection("mobile_money")}
                >
                  <FaMobile className="method-icon" />
                  <div className="method-info">
                    <span className="method-name">Mobile Money</span>
                    <span className="method-description">
                      Pay with MTN Mobile Money
                    </span>
                  </div>
                </button>
                <button
                  className="payment-method-btn card-btn"
                  onClick={() => handlePaymentMethodSelection("card")}
                >
                  <FaCreditCard className="method-icon" />
                  <div className="method-info">
                    <span className="method-name">Card Payment</span>
                    <span className="method-description">
                      Pay with Credit/Debit Card
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Money Instructions Modal */}
      {showMobileMoneyInstructions && (
        <div className="payment-method-selection-overlay">
          <div className="payment-method-selection-modal">
            <div className="payment-method-selection-header">
              <h3>Mobile Money Payment Instructions</h3>
              <button
                className="payment-method-selection-close"
                onClick={handleMobileMoneyInstructionsClose}
              >
                ×
              </button>
            </div>
            <div className="payment-method-selection-content">
              <div className="mobile-money-instructions">
                <div className="payment-method-header">
                  <FaMobile className="payment-icon" />
                  <h3>Mobile Money Payment</h3>
                </div>
                <div className="momo-code-section">
                  <span className="momo-label">MTN Mobile Money Code:</span>
                  <span className="momo-code">*182*8*1*052158#</span>
                  <span className="merchant-name">
                    SONG & STARK COOPERATION
                  </span>
                </div>
                <div className="payment-amount-info">
                  <p className="amount-label">Amount to Pay:</p>
                  <p className="amount-value">{formatMoney(orderAmount)}</p>
                </div>
                <div className="payment-instructions-footer">
                  <p className="payment-note">
                    Please complete the payment using the USSD code above to
                    process your order.
                  </p>
                  <div className="payment-buttons">
                    <button
                      onClick={handleBackToPaymentMethods}
                      className="back-btn"
                    >
                      Back to Payment Methods
                    </button>
                    <button
                      onClick={handleMobileMoneyInstructionsClose}
                      className="pay-now-btn"
                    >
                      Continue to Orders
                    </button>
                    <button
                      onClick={() => navigate("/contactus")}
                      className="contact-support-btn"
                    >
                      Need Help? Contact Support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Redirect URL Popup */}
      {showRedirectPopup && (
        <div className="redirect-popup-overlay">
          <div className="redirect-popup">
            <div className="redirect-popup-header">
              <h3>Payment Redirect Required</h3>
              <button
                className="redirect-popup-close"
                onClick={handleRedirectPopupClose}
              >
                ×
              </button>
            </div>
            <div className="redirect-popup-content">
              <p>
                Your order has been placed successfully. You can complete the
                payment now or continue to your orders.
              </p>
              <div className="redirect-popup-actions">
                <button
                  className="redirect-popup-button redirect-popup-button-primary"
                  onClick={handlePayNowClick}
                >
                  Pay Now
                </button>
                <button
                  className="redirect-popup-button redirect-popup-button-secondary"
                  onClick={handleRedirectPopupClose}
                >
                  Go to Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="checkoutPageContainer">
        <form className="formSectionContainer">
          <div className="expected-arrival">
            <FaTruck className="arrival-icon" />
            <div className="arrival-info">
              <span className="arrival-label">Expected Arrival</span>
              <span className="arrival-day">{expectedArrival.dayName}</span>
              <span className="arrival-date">{expectedArrival.date}</span>
            </div>
          </div>
          <div className="cart-items-container">
            {cartItems.map((item) => (
              <div
                className="cart-item"
                key={item.cart_item_id || item.product_id}
              >
                <img
                  src={getImage(item, item.product.imageUrl)}
                  alt={item.product.name}
                />
                <div className="amount-container">
                  <label>{item.product.name}</label>
                  {item.size?.name ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: "13px",
                        opacity: 0.85,
                      }}
                    >
                      Size: {item.size.name}
                    </span>
                  ) : null}
                  {item.selected_color ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: "13px",
                        opacity: 0.85,
                      }}
                    >
                      Color: {item.selected_color}
                    </span>
                  ) : null}
                  <div className="top-amount-container">
                    <span>{item.quantity}</span>
                    <span>x</span>
                    <span>{formatMoney(item.product.price)}</span>
                  </div>
                  <div className="bottom-amount-container">
                    <b>{formatMoney(item.product.price * item.quantity)}</b>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="formSection checkout-shipping">
            <div
              className="checkout-section-header"
              onClick={() => toggleSection("shipping")}
            >
              <div className="checkout-header">
                <h2 className="checkout-heading">
                  Shipping Information
                  {!defaultAddress && (
                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                  )}
                </h2>
                <span className="checkout-section-toggle">
                  {sectionIsActive("shipping") ? "-" : "+"}
                </span>
              </div>
              {isSectionFilled("shipping") && (
                <p className="section-summary">{contactAddress()}</p>
              )}
              {!isSectionFilled("shipping") && (
                <p
                  className="section-summary"
                  style={{ color: "red", fontSize: "14px" }}
                >
                  Please fill in and save your shipping address
                </p>
              )}
            </div>
            {sectionIsActive("shipping") && (
              <div className="shippingInfoForm">
                <ShippingInfo refetchAddress={refetchAddress} />
              </div>
            )}
          </div>
          <div className="formSection checkout-contact">
            <div
              className="checkout-section-header"
              onClick={() => toggleSection("contact")}
            >
              <div className="checkout-header">
                <h2 className="checkout-heading">
                  Contact Information
                  {!contactData.primaryPhone && (
                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                  )}
                </h2>
                <span className="checkout-section-toggle">
                  {sectionIsActive("contact") ? "-" : "+"}
                </span>
              </div>
              {isSectionFilled("contact") && (
                <p className="section-summary">
                  {`Primary: ${contactData.primaryPhone || ""}${
                    contactData.secondaryPhone
                      ? ` • Secondary: ${contactData.secondaryPhone}`
                      : ""
                  }`}
                </p>
              )}
              {!isSectionFilled("contact") && (
                <p
                  className="section-summary"
                  style={{ color: "red", fontSize: "14px" }}
                >
                  Please add your contact information
                </p>
              )}
            </div>
            <div className="contactInfoForm">
              <ContactInfo
                data={contactData}
                setData={setContactData}
                expanded={sectionIsActive("contact")}
              />
            </div>
          </div>
          <div className="shipping-fees">
            <ShippingFees />
          </div>
          <button
            type="submit"
            className="placeorder-submitButton"
            onClick={handleSubmit}
            disabled={isPlacingOrder}
          >
            {isPlacingOrder ? (
              <div className="button-loader">
                <div className="loader-spinner"></div>
                <span>Placing Order...</span>
              </div>
            ) : (
              "Place Order"
            )}
          </button>
        </form>
        <div className="orderSummarySection">
          <OrderSummary cartTotal={cartTotal} appliedCoupons={appliedCoupons} />
        </div>
      </div>
    </div>
  );
};

export default Checkout;
